import { CurrentPaletteType } from '../../../types';
import { useTexture } from '@react-three/drei';
import { useExperienceStore } from '../../../store';
import { useEffect, useState, useRef, useMemo } from 'react';
import { CONFIG } from '../../../config';
import { useThree, extend, useFrame } from '@react-three/fiber';
import { useMousePosition } from '@/hooks/use-mouse-position';
import {
  useSpring,
  easings,
  useSpringRef,
  useChain,
} from '@react-spring/three';
import { Color } from 'three';
import type { Group } from 'three';

import {
  CasePlaneMaterial,
  UniformTypes,
} from '../../../shaders/shader-materials';
import { NextRouter } from 'next/router';
import { lerp } from '@/utils/lerp';

interface CasePlaneProps {
  activePalette: CurrentPaletteType;
  backgroundColor: string;
  router: NextRouter;
  isRandom?: boolean;
}

extend({ CasePlaneMaterial });

export function CasePlane({
  activePalette,
  backgroundColor,
  router,
  isRandom = false,
}: CasePlaneProps) {
  const springApiOne = useSpringRef();
  const springApiTwo = useSpringRef();
  const springApiThree = useSpringRef();

  const groupRef = useRef<Group>(null);

  // Mouse stuff
  const mousePosition = useMousePosition({ normalize: true });
  const saveMousePosition = useRef({
    x: 0,
    y: 0,
  });

  const { camera, size, scene } = useThree();

  const texture = useTexture([activePalette.palette.image])[0];
  const [planeWidth, setPlaneWidth] = useState(1);
  const shaderMaterialRef = useRef<UniformTypes>(null);

  const { currentState } = useExperienceStore((state) => ({
    currentState: state.currentState,
  }));

  // An option to have multiple random directions
  const direction = useMemo(
    () => (isRandom ? (Math.random() > 0.5 ? 'normal' : 'reversed') : 'normal'),
    [isRandom]
  );

  // Find the color with the highest ratio in the palette so use for the transistion in the end
  const highestRatioItem = activePalette.palette.colors.reduce(
    (prev, current) => (prev.ratio > current.ratio ? prev : current)
  );

  const [properties, setProperties] = useState(() => ({
    segments: 142,
    segmentDivider: 13,
    lumaZ: -6,
    noiseFrequency: 2.5,
    noiseAmplitude: 0.05,
    slowMotion: 0.2,
  }));

  // Fix aspect ratio for the plane based on the camera Z distance
  // also fix the dot size and plane segments based on the canvas size
  useEffect(() => {
    if (!shaderMaterialRef.current || !texture) return;

    const shaderMaterial = shaderMaterialRef.current;

    // Scale
    const planeZ = 0;
    const distance = camera.position.z - planeZ;
    const aspect = size.width / size.height;
    const vFov = (CONFIG.SCREEN_WIDTH_PERCENTAGE * Math.PI) / 180;

    const planeHeightAtDistance =
      (2 * Math.tan(vFov / 2) * distance * texture.source.data.naturalHeight) /
      texture.source.data.naturalWidth;

    const planeWidthAtDistance = planeHeightAtDistance * aspect;

    setPlaneWidth(planeWidthAtDistance);

    const dotSizeValue = Math.log(size.width) / 2;

    setProperties((properties) => ({
      ...properties,
      segments: Math.round((size.width / properties.segmentDivider) * 3.5),
    }));

    shaderMaterial.uPointSize = dotSizeValue;

    shaderMaterial.uPointSize =
      shaderMaterial.uPointSize * (backgroundColor === 'white' ? 1.6 : 1.2);
  }, [
    camera.position.z,
    // @ts-expect-error fov exists
    camera.fov,
    size,
    properties.segmentDivider,
    texture,
    backgroundColor,
  ]);

  // Update shader when the props are updated,
  // this is mainly used when the leva settings are updated
  useEffect(() => {
    if (!shaderMaterialRef.current || !texture) return;

    const shaderMaterial = shaderMaterialRef.current;

    shaderMaterial.uTexture = texture;
    shaderMaterial.uLumaZ = properties.lumaZ;
    shaderMaterial.uNoiseAmplitude = properties.noiseAmplitude;
    shaderMaterial.uNoiseFrequency = properties.noiseFrequency;
    shaderMaterial.uSlowMotion = properties.slowMotion;

    shaderMaterial.uColorOutro = new Color(highestRatioItem.color);
  }, [
    texture,
    highestRatioItem,
    properties.lumaZ,
    properties.noiseFrequency,
    properties.noiseAmplitude,
    properties.slowMotion,
  ]);

  // Start animation
  useSpring({
    ref: springApiOne,
    from: {
      animationIntroProgress: 0,
      lumaZProgress: -3,
      frequency: 0,
      amplitude: 0,
      slowmotion: 0,
    },
    to: {
      animationIntroProgress: 1,
      lumaZProgress: 0.2,
      frequency: 4.5,
      amplitude: 1,
      slowmotion: 0.02,
    },
    config: {
      duration: 2500,
      easing: easings.easeOutQuint,
    },
    onChange: ({ value }) => {
      if (!shaderMaterialRef.current || currentState === 'explore') return;

      const shaderMaterial = shaderMaterialRef.current;

      shaderMaterial.uLumaZ = value.lumaZProgress;
      shaderMaterial.uProgress = value.animationIntroProgress;
      shaderMaterial.uOpacity = value.animationIntroProgress;

      shaderMaterial.uNoiseAmplitude = value.amplitude;
      shaderMaterial.uNoiseFrequency = value.frequency;
      shaderMaterial.uSlowMotion = value.slowmotion;
    },
  });

  // Start animation sequence 1
  useSpring({
    ref: springApiTwo,
    from: {
      animationIntroProgress: 1,
      lumaZProgress: 0.2,
      frequency: 4.5,
      amplitude: 1,
      slowmotion: 0.02,
    },
    to: {
      animationIntroProgress: 1,
      lumaZProgress: 0.2,
      frequency: 0.5,
      amplitude: 0.5,
      slowmotion: 0.02,
    },
    config: {
      duration: 3000,
      easing: easings.easeOutCirc,
    },
    onChange: ({ value }) => {
      if (!shaderMaterialRef.current || currentState === 'explore') return;
      const shaderMaterial = shaderMaterialRef.current;

      shaderMaterial.uLumaZ = value.lumaZProgress;

      shaderMaterial.uNoiseAmplitude = value.amplitude;
      shaderMaterial.uNoiseFrequency = value.frequency;
      shaderMaterial.uSlowMotion = value.slowmotion;
    },
  });

  // Start animation sequence 2
  useSpring({
    ref: springApiThree,
    from: {
      lumaZProgress: 0.2,
      frequency: 0.5,
      amplitude: 0.5,
      slowmotion: 0.02,
    },
    to: {
      lumaZProgress: 0.1,
      frequency: 0.8,
      amplitude: 0.2,
      slowmotion: 0.02,
    },
    config: {
      duration: 2500,
      easing: easings.easeOutCirc,
    },
    onChange: ({ value }) => {
      if (!shaderMaterialRef.current || currentState === 'explore') return;

      const shaderMaterial = shaderMaterialRef.current;

      shaderMaterial.uLumaZ = value.lumaZProgress;

      shaderMaterial.uNoiseAmplitude = value.amplitude;
      shaderMaterial.uNoiseFrequency = value.frequency;
      shaderMaterial.uSlowMotion = value.slowmotion;
    },
  });

  // Chain the animations together
  useChain([springApiOne, springApiTwo, springApiThree]);

  useSpring({
    progress: currentState === 'outro' ? 1 : 0,
    config: {
      mass: 4.2,
      tension: 316,
      friction: 12,
    },
    onChange: ({ value }) => {
      if (!shaderMaterialRef.current) return;

      const shaderMaterial = shaderMaterialRef.current;

      shaderMaterial.uProgressOutro = value.progress;
      shaderMaterial.uLumaZ = shaderMaterial.uLumaZ + value.progress * 0.5;
      shaderMaterial.uPointSize =
        shaderMaterial.uPointSize + value.progress * 0.85;
    },
  });

  // Outro animation back to explore
  useSpring({
    lumaZProgress:
      currentState === 'explore' ? (direction === 'normal' ? 0.2 : -1.5) : 0,
    config: {
      tension: 280,
      friction: 120,
      precision: 0.000001,
    },
    onChange: ({ value }) => {
      if (!shaderMaterialRef.current) return;
      const shaderMaterial = shaderMaterialRef.current;

      shaderMaterial.uLumaZ = shaderMaterial.uLumaZ + value.lumaZProgress * 0.3;
    },
  });

  useSpring({
    opacity: currentState === 'explore' ? 0 : 1,
    config: {
      duration: 800,
      delay: 200,
      easing: easings.easeOutCirc,
    },
    onChange: ({ value }) => {
      if (!shaderMaterialRef.current || currentState !== 'explore') return;
      const shaderMaterial = shaderMaterialRef.current;

      shaderMaterial.uOpacity = value.opacity;
    },
  });

  // Outro animation background
  useSpring({
    progress:
      currentState === 'outro' ? highestRatioItem.color : backgroundColor,
    config: {
      delay: 200,
      duration: 1500,
      easing: easings.easeInExpo,
    },
    onChange: ({ value }) => {
      if (currentState === 'outro') {
        scene.background = new Color(value.progress);
      }
    },
    onRest: () => {
      if (currentState === 'outro') {
        router.push(`/project/${activePalette.palette.slug}`);
      }
    },
  });

  useFrame(({ clock }) => {
    const shaderMaterial = shaderMaterialRef.current;
    const time = clock.getElapsedTime();

    if (!groupRef.current || !shaderMaterial) return;

    const lerpX = lerp(saveMousePosition.current.x, mousePosition.x, 0.03);
    const lerpY = lerp(saveMousePosition.current.y, mousePosition.y, 0.03);

    saveMousePosition.current.x = lerpX;
    saveMousePosition.current.y = lerpY;

    groupRef.current.rotation.y = lerpX * 0.1 * -1;
    groupRef.current.rotation.x = lerpY * 0.1 * -1;

    shaderMaterial.uNoiseFrequency += Math.sin(time) * 0.001;
    shaderMaterial.uNoiseAmplitude -= Math.cos(time) * 0.001;
  });

  if (currentState !== 'matched' && !texture) return null;

  return (
    <group ref={groupRef}>
      <points scale={planeWidth}>
        <planeGeometry
          args={[
            1,
            texture.source.data.naturalHeight /
              texture.source.data.naturalWidth,
            properties.segments,
            properties.segments,
          ]}
        />
        <casePlaneMaterial
          transparent={true}
          depthWrite={false}
          // @TODO Look into this type error
          // @ts-expect-error This type is incompatible with the expected type, but I don't know how to fix it
          ref={shaderMaterialRef}
        />
      </points>
    </group>
  );
}
